import React, { useState } from 'react';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import '../styles/FeedbackSection.css';

const IconChat = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const IconBug = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 2l1.5 1.5"/>
    <path d="M14.5 3.5 16 2"/>
    <path d="M9 7.5A3 3 0 0 1 15 7.5V13a3 3 0 0 1-6 0V7.5z"/>
    <path d="M6.5 9.5H4a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2.5"/>
    <path d="M17.5 9.5H20a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2.5"/>
    <path d="M9 17.5c0 1.5.5 2.5 3 2.5s3-1 3-2.5"/>
    <line x1="12" y1="7" x2="12" y2="13"/>
  </svg>
);

const IconSend = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);

const submitFeedbackFn = httpsCallable(functions, 'submitFeedback');

function FeedbackSection() {
  const [feedbackType, setFeedbackType] = useState('feedback');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState(null);

  const RATE_LIMIT_MINUTES = 10;

  const checkRateLimit = () => {
    if (!lastSubmitTime) return { allowed: true };
    const timeDiff = (new Date() - lastSubmitTime) / 1000 / 60;
    if (timeDiff < RATE_LIMIT_MINUTES) {
      return { allowed: false, remainingMinutes: Math.ceil(RATE_LIMIT_MINUTES - timeDiff) };
    }
    return { allowed: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (message.trim().length < 10) {
      alert('Please provide more details (at least 10 characters).');
      return;
    }
    if (message.trim().length > 1000) {
      alert('Message is too long. Please keep it under 1000 characters.');
      return;
    }

    const rateLimitCheck = checkRateLimit();
    if (!rateLimitCheck.allowed) {
      alert(`Please wait ${rateLimitCheck.remainingMinutes} minute(s) before submitting more feedback.`);
      return;
    }

    setIsSubmitting(true);
    try {
      await submitFeedbackFn({ feedbackType, message: message.trim() });
      setMessage('');
      setFeedbackType('feedback');
      setLastSubmitTime(new Date());
      alert(feedbackType === 'bug' ? 'Bug report submitted successfully!' : 'Feedback submitted successfully!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      // error.message comes straight from the Cloud Function's HttpsError (e.g.
      // "Daily feedback limit reached. Try again tomorrow."), so surface it
      // directly rather than always showing the generic fallback.
      alert(error?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="dashboard-header">
        <div>
          <h1>Report Bug & Feedback</h1>
          <p>Help us improve the Zella admin dashboard</p>
        </div>
      </div>

      <div className="alerts-section">
        <form onSubmit={handleSubmit}>
          <div className="form-section-feedback">
            <h3 className="form-section-title">Type</h3>
            <div className="feedback-type-grid">
              <div
                className={`feedback-type-card ${feedbackType === 'feedback' ? 'active' : ''}`}
                onClick={() => setFeedbackType('feedback')}
              >
                <div className="type-card-icon"><IconChat /></div>
                <div className="type-card-content">
                  <h4>Feedback</h4>
                  <p>Share your suggestions</p>
                </div>
              </div>
              <div
                className={`feedback-type-card ${feedbackType === 'bug' ? 'active' : ''}`}
                onClick={() => setFeedbackType('bug')}
              >
                <div className="type-card-icon"><IconBug /></div>
                <div className="type-card-content">
                  <h4>Bug Report</h4>
                  <p>Report an issue</p>
                </div>
              </div>
            </div>
          </div>

          <div className="form-section-feedback">
            <h3 className="form-section-title">Message</h3>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                feedbackType === 'bug'
                  ? 'Describe the bug you encountered in detail...'
                  : 'Share your thoughts, suggestions, or ideas...'
              }
              rows={10}
              maxLength={1000}
              className="feedback-textarea-large"
              required
            />
            <div className="textarea-meta">
              <span className="character-count-large">{message.length} / 1000 characters</span>
            </div>
          </div>

          <div className="form-actions-feedback">
            <button
              type="submit"
              disabled={isSubmitting || message.trim().length < 10}
              className="btn-submit-large"
            >
              {isSubmitting ? (
                <><span className="spinner-btn"></span>Submitting...</>
              ) : (
                <><IconSend />Submit {feedbackType === 'bug' ? 'Bug Report' : 'Feedback'}</>
              )}
            </button>
            {message.trim().length < 10 && message.length > 0 && (
              <span className="submit-hint-text">Minimum 10 characters required</span>
            )}
          </div>
        </form>
      </div>
    </>
  );
}

export default FeedbackSection;